import { useCallback, useEffect, useMemo, useState } from "react";
import { formatUzPhone } from "../../phone";
import {
  api,
  type ClientReminderType,
  type ExpenseCategory,
  type ExpenseOut,
  type FuelEconomyOut,
  type MergedCarDetailOut,
  type MergedCarOut,
  type Period,
  type ReminderListItem,
  type UserOut,
  type WorkshopContactOut,
} from "../api";
import {
  haptic,
  openExternal,
  useBackButton,
  useMainButton,
} from "../telegram";
import {
  formatPrice,
  groupDigits,
  relativeDate,
  todayIso,
  untilLabel,
} from "../util";

// ---------------------------------------------------------------------------
// Kategoriyalar va yorliqlar
// ---------------------------------------------------------------------------

type CatDef = { code: ExpenseCategory; label: string; icon: string };

const CATEGORIES: CatDef[] = [
  { code: "fuel", label: "Yoqilg'i", icon: "⛽" },
  { code: "oil", label: "Moy", icon: "🛢️" },
  { code: "repair_external", label: "Ta'mir", icon: "🔧" },
  { code: "parts", label: "Qism", icon: "⚙️" },
  { code: "tire", label: "Shina", icon: "🛞" },
  { code: "insurance", label: "Sug'urta", icon: "🛡️" },
  { code: "carwash", label: "Yuvish", icon: "💧" },
  { code: "inspection", label: "Tex ko'rik", icon: "📋" },
  { code: "fine", label: "Jarima", icon: "🚦" },
  { code: "other", label: "Boshqa", icon: "➕" },
];

const WORKS_CATEGORY = "ustaxona_xizmati";
const FUEL_TYPES = ["benzin", "metan", "propan"];

function catDef(code: string): CatDef | null {
  return CATEGORIES.find((c) => c.code === code) ?? null;
}

function categoryLabel(code: string): string {
  if (code === WORKS_CATEGORY) return "Ustaxona xizmati";
  return catDef(code)?.label ?? code;
}

function categoryIcon(code: string): string {
  if (code === WORKS_CATEGORY) return "🔧";
  return catDef(code)?.icon ?? "•";
}

function expenseTitle(e: ExpenseOut): string {
  if (e.category === "other") return e.custom_label || "Boshqa";
  let t = categoryLabel(e.category);
  if (e.category === "fuel" && e.fuel_type) {
    t += ` (${e.fuel_type})`;
  }
  return t;
}

const BAR_COLORS = [
  "var(--primary)",
  "#2e9e6b",
  "#d18908",
  "#7c5cff",
  "#d23b3b",
  "#0aa6b8",
  "#c2569b",
];

// Kategoriya ishlatilish chastotasi — tez kiritish uchun (localStorage).
const USAGE_KEY = "an_cat_usage";
const LASTCAT_KEY = "an_last_cat";

function loadUsage(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(USAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function bumpUsage(plate: string, code: ExpenseCategory) {
  const u = loadUsage();
  u[code] = (u[code] || 0) + 1;
  localStorage.setItem(USAGE_KEY, JSON.stringify(u));
  try {
    const last = JSON.parse(localStorage.getItem(LASTCAT_KEY) || "{}");
    last[plate] = code;
    localStorage.setItem(LASTCAT_KEY, JSON.stringify(last));
  } catch {
    /* ignore */
  }
}

function lastCategory(plate: string): ExpenseCategory | null {
  try {
    const last = JSON.parse(localStorage.getItem(LASTCAT_KEY) || "{}");
    return last[plate] || null;
  } catch {
    return null;
  }
}

function orderedCategories(): CatDef[] {
  const u = loadUsage();
  return [...CATEGORIES].sort((a, b) => {
    if (a.code === "other") return 1;
    if (b.code === "other") return -1;
    return (u[b.code] || 0) - (u[a.code] || 0);
  });
}

// ---------------------------------------------------------------------------
// Navigatsiya stack
// ---------------------------------------------------------------------------

type Tab = "garage" | "reminders" | "workshops" | "settings";

type View =
  | { name: "car"; plate: string }
  | { name: "add-expense"; plate?: string; editing?: ExpenseOut }
  | { name: "add-reminder"; editing?: ReminderListItem }
  | { name: "car-form"; editing?: MergedCarOut }
  | { name: "workshop"; workshop: WorkshopContactOut };

export function ClientHomeScreen({ user }: { user: UserOut }) {
  const [tab, setTab] = useState<Tab>("garage");
  const [stack, setStack] = useState<View[]>([]);

  const push = useCallback((v: View) => setStack((s) => [...s, v]), []);
  const pop = useCallback(() => setStack((s) => s.slice(0, -1)), []);
  const top = stack.length ? stack[stack.length - 1] : null;

  useBackButton(stack.length > 0, pop);

  const goTab = (t: Tab) => {
    setStack([]);
    setTab(t);
  };

  return (
    <div className="app">
      {top ? (
        <NestedView view={top} push={push} pop={pop} goTab={goTab} />
      ) : (
        <>
          {tab === "garage" && (
            <GarageScreen onBell={() => goTab("reminders")} push={push} />
          )}
          {tab === "reminders" && <RemindersScreen push={push} />}
          {tab === "workshops" && <WorkshopsScreen push={push} />}
          {tab === "settings" && <SettingsScreen user={user} />}
          <BottomNav tab={tab} setTab={goTab} />
        </>
      )}
    </div>
  );
}

function NestedView({
  view,
  push,
  pop,
  goTab,
}: {
  view: View;
  push: (v: View) => void;
  pop: () => void;
  goTab: (t: Tab) => void;
}) {
  if (view.name === "car")
    return <CarDetailScreen plate={view.plate} push={push} pop={pop} />;
  if (view.name === "add-expense")
    return (
      <AddExpenseScreen
        plate={view.plate}
        editing={view.editing}
        onDone={pop}
      />
    );
  if (view.name === "add-reminder")
    return <AddReminderScreen editing={view.editing} onDone={pop} />;
  if (view.name === "car-form")
    return <CarFormScreen editing={view.editing} onDone={pop} />;
  if (view.name === "workshop")
    return <WorkshopDetailScreen workshop={view.workshop} pop={pop} />;
  // never
  void goTab;
  return null;
}

// ---------------------------------------------------------------------------
// Umumiy yordamchilar
// ---------------------------------------------------------------------------

function useAsync<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => alive && setData(d))
      .catch(
        (e) => alive && setError((e as { message?: string }).message || "Xatolik"),
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, error, loading };
}

function Loader() {
  return (
    <div className="center">
      <div className="spinner" />
    </div>
  );
}

function AppBar({
  title,
  onBack,
  onBell,
}: {
  title: string;
  onBack?: () => void;
  onBell?: () => void;
}) {
  return (
    <div className="appbar">
      <div className="appbar-title">
        {onBack ? (
          <button className="appbar-back" onClick={onBack} aria-label="Orqaga">
            ‹
          </button>
        ) : (
          <span>🚗</span>
        )}
        <span>{title}</span>
      </div>
      {onBell && (
        <button className="appbar-back" onClick={onBell} aria-label="Eslatmalar">
          🔔
        </button>
      )}
    </div>
  );
}

function BottomNav({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  const items: { id: Tab; ico: string; label: string }[] = [
    { id: "garage", ico: "🚗", label: "Garaj" },
    { id: "reminders", ico: "🔔", label: "Eslatmalar" },
    { id: "workshops", ico: "🔧", label: "Ustaxonalar" },
    { id: "settings", ico: "⚙️", label: "Sozlamalar" },
  ];
  return (
    <nav className="bottom-nav">
      {items.map((it) => (
        <button
          key={it.id}
          className={`nav-item ${tab === it.id ? "active" : ""}`}
          onClick={() => setTab(it.id)}
        >
          <span className="nav-ico">{it.ico}</span>
          <span className="nav-label">{it.label}</span>
        </button>
      ))}
    </nav>
  );
}

/** Telegram MainButton yo'q bo'lsa qotirilgan in-app harakat tugmasi. */
function BottomAction({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <div className="bottom-action">
      <button className="btn" onClick={onClick}>
        + {label}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Garaj
// ---------------------------------------------------------------------------

function GarageScreen({
  onBell,
  push,
}: {
  onBell: () => void;
  push: (v: View) => void;
}) {
  const { data, error, loading } = useAsync<MergedCarOut[]>(
    () => api.clientGarage(),
    [],
  );

  return (
    <>
      <AppBar title="Mening Garajim" onBell={onBell} />
      <div className="screen">
        <p className="sub">Avtomobillaringiz va xarajatlari</p>

        {loading ? (
          <>
            <div className="skeleton" />
            <div className="skeleton" />
          </>
        ) : error ? (
          <div className="empty">{error}</div>
        ) : !data || data.length === 0 ? (
          <div className="empty-hero">
            <div className="empty-ico">🚗</div>
            <div className="empty-title">Hali avto yo'q</div>
            <div className="empty-text">
              Avtomobilingizni qo'shing va xarajatlarini kuzatib boring.
            </div>
            <button className="btn" onClick={() => push({ name: "car-form" })}>
              Avtomobil qo'shish
            </button>
          </div>
        ) : (
          <>
            {data.map((c) => (
            <div key={c.normalized_plate} className="car-card">
              <div
                onClick={() => push({ name: "car", plate: c.normalized_plate })}
              >
                <div className="car-card-head">
                  <div>
                    <div className="car-name">{c.model || "Avtomobil"}</div>
                    {c.year && <div className="sub">{c.year} yil</div>}
                  </div>
                  <div className="plate-badge">{c.display_plate}</div>
                </div>
                <div className="car-total-row">
                  <span className="sub">Jami xarajat</span>
                  <span className="car-total-value">
                    {formatPrice(c.total_spent)} so'm
                  </span>
                </div>
                {c.next_service_hint && (
                  <div className="car-hint">
                    ⏰ {c.next_service_hint.service_name} ·{" "}
                    {relativeDate(c.next_service_hint.due_at)}
                  </div>
                )}
              </div>
              <div className="car-actions">
                <button
                  className="mini"
                  onClick={() =>
                    push({ name: "add-expense", plate: c.normalized_plate })
                  }
                >
                  ⛽ Xarajat
                </button>
                {c.is_self_added && (
                  <button
                    className="mini"
                    onClick={() => push({ name: "car-form", editing: c })}
                  >
                    ✎ Tahrir
                  </button>
                )}
              </div>
            </div>
            ))}
            <button
              className="btn btn-ghost"
              onClick={() => push({ name: "car-form" })}
            >
              + Avtomobil qo'shish
            </button>
          </>
        )}
      </div>
      {data && data.length > 0 && (
        <BottomAction
          label="Xarajat qo'shish"
          onClick={() => push({ name: "add-expense" })}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Car detail
// ---------------------------------------------------------------------------

function CarDetailScreen({
  plate,
  push,
  pop,
}: {
  plate: string;
  push: (v: View) => void;
  pop: () => void;
}) {
  const [period, setPeriod] = useState<Period>("month");
  const [reload, setReload] = useState(0);
  const { data, error, loading } = useAsync<MergedCarDetailOut>(
    () => api.clientGarageDetail(plate, period),
    [plate, period, reload],
  );
  const fuel = useAsync<FuelEconomyOut>(
    () => api.clientFuelEconomy(plate),
    [plate, reload],
  );
  const contacts = useAsync<WorkshopContactOut[]>(
    () => api.clientCarWorkshops(plate),
    [plate, reload],
  );
  const [showAll, setShowAll] = useState(false);
  // Ustaxona bo'yicha xabar opt-out holati (workshop_id ro'yxati).
  const [optedOut, setOptedOut] = useState<number[]>([]);
  const [optSaving, setOptSaving] = useState<number | null>(null);
  useEffect(() => {
    api
      .clientOptOuts()
      .then(setOptedOut)
      .catch(() => setOptedOut([]));
  }, [reload]);
  const toggleOptOut = async (workshopId: number) => {
    const isOut = optedOut.includes(workshopId);
    setOptSaving(workshopId);
    try {
      if (isOut) {
        await api.clientOptIn(workshopId);
        setOptedOut((s) => s.filter((x) => x !== workshopId));
      } else {
        await api.clientOptOut(workshopId);
        setOptedOut((s) => [...s, workshopId]);
      }
    } catch {
      /* tarmoq xatosi — holatni o'zgartirmaymiz */
    } finally {
      setOptSaving(null);
    }
  };

  const onDeleteExpense = async (id: number) => {
    if (!window.confirm("Xarajat o'chirilsinmi?")) return;
    await api.clientDeleteExpense(id);
    setReload((r) => r + 1);
  };

  return (
    <>
      <AppBar title="Avtomobil" onBack={pop} />
      <div className="screen">
        {loading ? (
          <Loader />
        ) : error ? (
          <div className="empty">{error}</div>
        ) : data ? (
          <>
            <h1 className="h1">{data.model || data.display_plate}</h1>
            <p className="sub">
              {data.display_plate}
              {data.year ? ` · ${data.year} yil` : ""}
            </p>

            <div className="summary-grid">
              <div className="summary-card">
                <div className="summary-label">Shu oy xarajatlari</div>
                <div className="summary-value accent">
                  {formatPrice(data.month_total)} <small>so'm</small>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Barcha vaqt</div>
                <div className="summary-value">
                  {formatPrice(data.total_spent)} <small>so'm</small>
                </div>
              </div>
            </div>

            {/* Xarajatlar tarkibi */}
            <div className="panel">
              <div className="panel-head">
                <h2 className="h2">Xarajatlar tarkibi</h2>
                <div className="seg">
                  {(["month", "year", "all"] as Period[]).map((p) => (
                    <button
                      key={p}
                      className={period === p ? "active" : ""}
                      onClick={() => setPeriod(p)}
                    >
                      {p === "month" ? "Oy" : p === "year" ? "Yil" : "Hammasi"}
                    </button>
                  ))}
                </div>
              </div>
              {data.breakdown.length === 0 ? (
                <div className="muted">Bu davrda xarajat yo'q.</div>
              ) : (
                data.breakdown
                  .slice()
                  .sort((a, b) => b.total - a.total)
                  .map((b, i) => {
                    const pct =
                      data.period_total > 0
                        ? Math.round((b.total / data.period_total) * 100)
                        : 0;
                    return (
                      <div className="bar-row" key={b.category}>
                        <div className="bar-top">
                          <span className="bar-label">
                            {categoryIcon(b.category)} {categoryLabel(b.category)}
                          </span>
                          <span className="bar-amt">
                            {formatPrice(b.total)} so'm · {pct}%
                          </span>
                        </div>
                        <div className="bar-track">
                          <div
                            className="bar-fill"
                            style={{
                              width: `${pct}%`,
                              background: BAR_COLORS[i % BAR_COLORS.length],
                            }}
                          />
                        </div>
                      </div>
                    );
                  })
              )}
            </div>

            {/* Yoqilg'i tejamkorligi */}
            {fuel.data?.has_data && (
              <div className="panel">
                <h2 className="h2" style={{ marginBottom: 12 }}>
                  ⛽ Yoqilg'i tejamkorligi
                </h2>
                <div className="fuel-stat">
                  <div className="fuel-stat-item">
                    <div className="fuel-stat-value">
                      {fuel.data.avg_l_per_100km ?? "—"}
                    </div>
                    <div className="fuel-stat-label">litr / 100 km (o'rtacha)</div>
                  </div>
                  {fuel.data.avg_cost_per_km != null && (
                    <div className="fuel-stat-item">
                      <div className="fuel-stat-value">
                        {formatPrice(Math.round(fuel.data.avg_cost_per_km))}
                      </div>
                      <div className="fuel-stat-label">so'm / km</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Ustaxona aloqasi */}
            {contacts.data?.map((w) => (
              <div className="contact-card" key={w.workshop_id}>
                <div>
                  <div className="contact-sub">Mening ustaxonam</div>
                  <div className="contact-name">{w.name}</div>
                  {w.phone && <div className="contact-sub">{formatUzPhone(w.phone)}</div>}
                  <label className="opt-out-row">
                    <input
                      type="checkbox"
                      checked={!optedOut.includes(w.workshop_id)}
                      disabled={optSaving === w.workshop_id}
                      onChange={() => toggleOptOut(w.workshop_id)}
                    />
                    <span>Xabarlarni qabul qilish</span>
                  </label>
                </div>
                {w.phone && (
                  <button
                    className="contact-call"
                    onClick={() => openExternal(`tel:${w.phone}`)}
                    aria-label="Qo'ng'iroq"
                  >
                    📞
                  </button>
                )}
              </div>
            ))}

            {/* Tarix tasmasi */}
            <div className="section-head">
              <h2 className="h2">So'nggi amallar</h2>
              {data.timeline.length > 5 && (
                <button
                  className="link-btn"
                  onClick={() => setShowAll((v) => !v)}
                >
                  {showAll ? "Kamroq" : "Hammasi"}
                </button>
              )}
            </div>
            {data.timeline.length === 0 ? (
              <div className="empty">
                Hali yozuv yo'q. Birinchi xarajatingizni qo'shing.
              </div>
            ) : (
              (showAll ? data.timeline : data.timeline.slice(0, 5)).map((it) =>
                it.kind === "work" ? (
                  <div key={`w${it.id}`} className="feed-item workshop">
                    <div className="feed-ico">{categoryIcon(WORKS_CATEGORY)}</div>
                    <div className="feed-main">
                      <div className="feed-title">{it.service_name}</div>
                      <div className="feed-sub">
                        ✓ {it.workshop_name} · {relativeDate(it.date)}
                      </div>
                    </div>
                    <div className="feed-amount">
                      -{formatPrice(it.price)} so'm
                    </div>
                  </div>
                ) : (
                  <div key={`e${it.expense.id}`} className="feed-item">
                    <div className="feed-ico">
                      {categoryIcon(it.expense.category)}
                    </div>
                    <div className="feed-main">
                      <div className="feed-title">{expenseTitle(it.expense)}</div>
                      <div className="feed-sub">
                        {relativeDate(it.expense.spent_at)} · Men kiritdim
                        {it.expense.odometer_km
                          ? ` · ${formatPrice(it.expense.odometer_km)} km`
                          : ""}
                      </div>
                      <div className="feed-actions">
                        <button
                          className="link-btn"
                          onClick={() =>
                            push({
                              name: "add-expense",
                              plate,
                              editing: it.expense,
                            })
                          }
                        >
                          Tahrir
                        </button>
                        <button
                          className="link-btn"
                          style={{ color: "var(--destructive)" }}
                          onClick={() => onDeleteExpense(it.expense.id)}
                        >
                          O'chirish
                        </button>
                      </div>
                    </div>
                    <div className="feed-amount">
                      -{formatPrice(it.expense.amount)} so'm
                    </div>
                  </div>
                ),
              )
            )}
          </>
        ) : null}
      </div>
      <BottomAction
        label="Xarajat qo'shish"
        onClick={() => push({ name: "add-expense", plate })}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Add / edit expense (full screen, Telegram MainButton)
// ---------------------------------------------------------------------------

function AddExpenseScreen({
  plate,
  editing,
  onDone,
}: {
  plate?: string;
  editing?: ExpenseOut;
  onDone: () => void;
}) {
  const garage = useAsync<MergedCarOut[]>(() => api.clientGarage(), []);
  const cars = garage.data ?? [];
  const initialPlate = editing?.normalized_plate || plate || "";

  const [selPlate, setSelPlate] = useState(initialPlate);
  const [category, setCategory] = useState<ExpenseCategory>(
    editing?.category || lastCategory(initialPlate) || "fuel",
  );
  const [expanded, setExpanded] = useState(false);
  const [customLabel, setCustomLabel] = useState(editing?.custom_label || "");
  const [amountRaw, setAmountRaw] = useState(
    editing ? String(editing.amount) : "",
  );
  const [spentAt, setSpentAt] = useState(editing?.spent_at || todayIso());
  const [odometer, setOdometer] = useState(
    editing?.odometer_km != null ? String(editing.odometer_km) : "",
  );
  const [litr, setLitr] = useState(editing?.litr != null ? String(editing.litr) : "");
  const [fuelType, setFuelType] = useState<string | null>(
    editing?.fuel_type || null,
  );
  const [note, setNote] = useState(editing?.note || "");
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Avto tanlanmagan bo'lsa, birinchi avtoni tanlaymiz.
  useEffect(() => {
    if (!selPlate && cars.length > 0) setSelPlate(cars[0].normalized_plate);
  }, [cars, selPlate]);

  const ordered = useMemo(() => orderedCategories(), []);
  const shown = expanded ? ordered : ordered.slice(0, 7);

  const amountDisplay = groupDigits(amountRaw);

  const submit = useCallback(async () => {
    const amount = Number(amountRaw);
    if (!selPlate) {
      setErr("Avtoni tanlang");
      return;
    }
    if (!amount || amount <= 0) {
      setErr("Summa 0 dan katta bo'lishi kerak");
      return;
    }
    if (category === "other" && !customLabel.trim()) {
      setErr('"Boshqa" uchun izoh kiriting');
      return;
    }
    if (spentAt > todayIso()) {
      setErr("Sana kelajakda bo'lishi mumkin emas");
      return;
    }
    const payload = {
      category,
      custom_label: category === "other" ? customLabel.trim() : null,
      amount,
      spent_at: spentAt,
      note: note.trim() || null,
      odometer_km: odometer ? Number(odometer) : null,
      litr: category === "fuel" && litr ? Number(litr) : null,
      fuel_type: category === "fuel" ? fuelType : null,
    };
    setSaving(true);
    setErr(null);
    try {
      if (editing) {
        await api.clientUpdateExpense(editing.id, payload);
      } else {
        await api.clientCreateExpense({ ...payload, plate: selPlate });
      }
      bumpUsage(selPlate, category);
      haptic("success");
      onDone();
    } catch (e) {
      setErr((e as { message?: string }).message || "Xatolik");
      setSaving(false);
    }
  }, [
    amountRaw, selPlate, category, customLabel, spentAt, note, odometer,
    litr, fuelType, editing, onDone,
  ]);

  const hasMainButton = useMainButton({
    text: saving ? "Saqlanmoqda…" : "Saqlash",
    onClick: submit,
    loading: saving,
  });

  return (
    <>
      <AppBar title={editing ? "Xarajatni tahrirlash" : "Xarajat qo'shish"} onBack={onDone} />
      <div className="screen">
        {/* Avto tanlash — 2+ avto bo'lsa */}
        {!editing && cars.length >= 2 && (
          <>
            <label className="label">Avtomobil</label>
            <div className="chip-row">
              {cars.map((c) => (
                <button
                  key={c.normalized_plate}
                  className={`chip ${selPlate === c.normalized_plate ? "active" : ""}`}
                  onClick={() => setSelPlate(c.normalized_plate)}
                >
                  {c.display_plate}
                </button>
              ))}
            </div>
          </>
        )}

        <label className="label">Kategoriya</label>
        <div className="cat-grid">
          {shown.map((c) => (
            <button
              key={c.code}
              className={`cat-btn ${category === c.code ? "active" : ""}`}
              onClick={() => setCategory(c.code)}
            >
              <span className="cat-ico">{c.icon}</span>
              <span className="cat-label">{c.label}</span>
            </button>
          ))}
        </div>
        {ordered.length > 7 && (
          <button className="more-btn" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Kamroq ▲" : "Boshqa / ko'proq ▼"}
          </button>
        )}

        {category === "other" && (
          <>
            <label className="label">Izoh (turi)</label>
            <input
              className="input"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="Masalan: Yo'l boji"
            />
          </>
        )}

        {/* Summa */}
        <div className="amount-box">
          <input
            className="amount-input"
            inputMode="numeric"
            value={amountDisplay}
            onChange={(e) => setAmountRaw(e.target.value.replace(/\D/g, ""))}
            placeholder="0"
          />
          <div className="amount-unit">so'm</div>
        </div>

        {/* Yoqilg'i tafsilotlari */}
        {category === "fuel" && (
          <div className="fuel-panel">
            <div className="fuel-panel-title">⛽ Yoqilg'i tafsilotlari</div>
            <div className="field-grid">
              <div>
                <label className="label" style={{ marginTop: 0 }}>
                  Hajm (litr)
                </label>
                <input
                  className="input"
                  inputMode="decimal"
                  value={litr}
                  onChange={(e) =>
                    setLitr(e.target.value.replace(/[^\d.]/g, ""))
                  }
                  placeholder="35"
                />
              </div>
              <div>
                <label className="label" style={{ marginTop: 0 }}>
                  Turi
                </label>
                <div className="seg" style={{ width: "100%" }}>
                  {FUEL_TYPES.map((ft) => (
                    <button
                      key={ft}
                      className={fuelType === ft ? "active" : ""}
                      style={{ flex: 1, textTransform: "capitalize" }}
                      onClick={() => setFuelType(fuelType === ft ? null : ft)}
                    >
                      {ft}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <label className="label">Sana</label>
        <input
          className="input"
          type="date"
          value={spentAt}
          max={todayIso()}
          onChange={(e) => setSpentAt(e.target.value)}
        />

        <label className="label">Kilometr (ixtiyoriy)</label>
        <input
          className="input"
          inputMode="numeric"
          value={odometer ? groupDigits(odometer) : ""}
          onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ""))}
          placeholder="Joriy probeg"
        />

        <label className="label">Izoh (ixtiyoriy)</label>
        <textarea
          className="textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Qo'shimcha ma'lumot…"
        />

        {err && <div className="error-text">{err}</div>}

        {!hasMainButton && (
          <button className="btn" onClick={submit} disabled={saving}>
            {saving ? "Saqlanmoqda…" : "Saqlash"}
          </button>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Manual car form (add/edit) — keep existing capability
// ---------------------------------------------------------------------------

function CarFormScreen({
  editing,
  onDone,
}: {
  editing?: MergedCarOut;
  onDone: () => void;
}) {
  const [plate, setPlate] = useState(editing?.display_plate || "");
  const [model, setModel] = useState(editing?.model || "");
  const [year, setYear] = useState(editing?.year ? String(editing.year) : "");
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = useCallback(async () => {
    if (!plate.trim()) {
      setErr("Avto raqamini kiriting");
      return;
    }
    const payload = {
      plate: plate.trim(),
      model: model.trim() || null,
      year: year ? Number(year) : null,
    };
    setSaving(true);
    setErr(null);
    try {
      if (editing && editing.client_car_id != null) {
        await api.clientUpdateCar(editing.client_car_id, payload);
      } else {
        await api.clientCreateCar(payload);
      }
      haptic("success");
      onDone();
    } catch (e) {
      setErr((e as { message?: string }).message || "Xatolik");
      setSaving(false);
    }
  }, [plate, model, year, editing, onDone]);

  const onDelete = async () => {
    if (editing?.client_car_id == null) return;
    if (!window.confirm("Avto o'chirilsinmi?")) return;
    await api.clientDeleteCar(editing.client_car_id);
    onDone();
  };

  const hasMainButton = useMainButton({
    text: saving ? "Saqlanmoqda…" : "Saqlash",
    onClick: submit,
    loading: saving,
  });

  return (
    <>
      <AppBar title={editing ? "Avtoni tahrirlash" : "Avtomobil qo'shish"} onBack={onDone} />
      <div className="screen">
        <label className="label">Avto raqami</label>
        <input
          className="input"
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          placeholder="01 A 777 BC"
        />
        <label className="label">Model (ixtiyoriy)</label>
        <input
          className="input"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="Masalan: Cobalt"
        />
        <label className="label">Yil (ixtiyoriy)</label>
        <input
          className="input"
          inputMode="numeric"
          value={year}
          onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
          placeholder="2021"
        />

        {err && <div className="error-text">{err}</div>}

        {!hasMainButton && (
          <button className="btn" onClick={submit} disabled={saving}>
            {saving ? "Saqlanmoqda…" : "Saqlash"}
          </button>
        )}
        {editing && editing.client_car_id != null && (
          <button
            className="btn btn-secondary"
            style={{ color: "var(--destructive)" }}
            onClick={onDelete}
          >
            O'chirish
          </button>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Reminders
// ---------------------------------------------------------------------------

const STATUS_META: Record<
  string,
  { label: string; varName: string }
> = {
  overdue: { label: "Muddati o'tgan", varName: "--st-overdue" },
  due_soon: { label: "Yaqinlashmoqda", varName: "--st-due_soon" },
  upcoming: { label: "Rejada", varName: "--st-upcoming" },
  scheduled: { label: "Rejada", varName: "--st-scheduled" },
};

function reminderIcon(it: ReminderListItem): string {
  if (it.type) {
    const m: Record<ClientReminderType, string> = {
      sugurta: "🛡️",
      texnik_korik: "🔍",
      moy: "🛢️",
      boshqa: "🔔",
    };
    return m[it.type];
  }
  return "🔧";
}

function RemindersScreen({ push }: { push: (v: View) => void }) {
  const [reload, setReload] = useState(0);
  const { data, error, loading } = useAsync<ReminderListItem[]>(
    () => api.clientReminders(),
    [reload],
  );

  const onDelete = async (id: number) => {
    if (!window.confirm("Eslatma o'chirilsinmi?")) return;
    await api.clientDeleteReminder(id);
    setReload((r) => r + 1);
  };

  return (
    <>
      <AppBar title="Eslatmalar" />
      <div className="screen">
        <p className="sub">Rejalashtirilgan xizmatlar va to'lovlar</p>

        {loading ? (
          <>
            <div className="skeleton" />
            <div className="skeleton" />
          </>
        ) : error ? (
          <div className="empty">{error}</div>
        ) : !data || data.length === 0 ? (
          <div className="empty-hero">
            <div className="empty-ico">🔔</div>
            <div className="empty-title">Hali eslatma yo'q</div>
            <div className="empty-text">
              Sug'urta yoki texnik ko'rik muddatini qo'shing — vaqti kelganda
              sizga xabar beramiz.
            </div>
            <button
              className="btn"
              onClick={() => push({ name: "add-reminder" })}
            >
              Eslatma qo'shish
            </button>
          </div>
        ) : (
          data.map((it) => {
            const meta = STATUS_META[it.status] || STATUS_META.upcoming;
            const accent = `var(${meta.varName})`;
            return (
              <div
                key={`${it.source}-${it.id}`}
                className="reminder-card"
                style={{ ["--accent" as string]: accent }}
              >
                <div className="reminder-top">
                  <div>
                    <div className="reminder-title">
                      <span>{reminderIcon(it)}</span> {it.title}
                    </div>
                    {it.source === "workshop" ? (
                      <div className="reminder-source shop">
                        ✓ {it.workshop_name}
                      </div>
                    ) : (
                      <div className="reminder-source">👤 Men kiritdim</div>
                    )}
                  </div>
                  <span className="status-badge" style={{ color: accent }}>
                    {meta.label}
                  </span>
                </div>
                <div className="reminder-meta">
                  {it.due_at && <span>📅 {untilLabel(it.due_at)}</span>}
                  {it.due_km != null && (
                    <span>
                      🛣️ {formatPrice(it.due_km)} km
                      {it.current_km != null
                        ? ` (qoldi ${formatPrice(
                            Math.max(0, it.due_km - it.current_km),
                          )} km)`
                        : ""}
                    </span>
                  )}
                  {it.note && <span>📝 {it.note}</span>}
                </div>
                {it.editable && (
                  <div className="reminder-actions">
                    <button
                      className="link-btn"
                      onClick={() =>
                        push({ name: "add-reminder", editing: it })
                      }
                    >
                      Tahrir
                    </button>
                    <button
                      className="link-btn"
                      style={{ color: "var(--destructive)" }}
                      onClick={() => onDelete(it.id)}
                    >
                      O'chirish
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {data && data.length > 0 && (
        <BottomAction
          label="Eslatma qo'shish"
          onClick={() => push({ name: "add-reminder" })}
        />
      )}
    </>
  );
}

const REMINDER_TYPES: { code: ClientReminderType; label: string; icon: string }[] =
  [
    { code: "sugurta", label: "Sug'urta", icon: "🛡️" },
    { code: "texnik_korik", label: "Texnik ko'rik", icon: "🔍" },
    { code: "moy", label: "Moy almashtirish", icon: "🛢️" },
    { code: "boshqa", label: "Boshqa", icon: "🔔" },
  ];

function AddReminderScreen({
  editing,
  onDone,
}: {
  editing?: ReminderListItem;
  onDone: () => void;
}) {
  const garage = useAsync<MergedCarOut[]>(() => api.clientGarage(), []);
  const cars = garage.data ?? [];

  const [type, setType] = useState<ClientReminderType>(
    editing?.type || "sugurta",
  );
  const [selPlate, setSelPlate] = useState("");
  const [dueDate, setDueDate] = useState(
    editing?.due_at ? editing.due_at.slice(0, 10) : "",
  );
  const [dueKm, setDueKm] = useState(
    editing?.due_km != null ? String(editing.due_km) : "",
  );
  const [note, setNote] = useState(editing?.note || "");
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Avto tanlash: tahrirlashda mavjud raqamga moslaymiz, aks holda birinchisi.
  useEffect(() => {
    if (selPlate || cars.length === 0) return;
    if (editing) {
      const match = cars.find(
        (c) => c.display_plate === editing.plate || c.normalized_plate === editing.plate,
      );
      setSelPlate(match?.normalized_plate || cars[0].normalized_plate);
    } else {
      setSelPlate(cars[0].normalized_plate);
    }
  }, [cars, editing, selPlate]);

  const submit = useCallback(async () => {
    if (!selPlate) {
      setErr("Avtoni tanlang");
      return;
    }
    if (!dueDate && !dueKm) {
      setErr("Sana yoki masofa (km) kiriting");
      return;
    }
    const payload = {
      plate: selPlate,
      type,
      title: null,
      due_at: dueDate ? new Date(dueDate + "T09:00:00").toISOString() : null,
      due_km: dueKm ? Number(dueKm) : null,
      note: note.trim() || null,
    };
    setSaving(true);
    setErr(null);
    try {
      if (editing && editing.source === "client") {
        await api.clientUpdateReminder(editing.id, payload);
      } else {
        await api.clientCreateReminder(payload);
      }
      haptic("success");
      onDone();
    } catch (e) {
      setErr((e as { message?: string }).message || "Xatolik");
      setSaving(false);
    }
  }, [selPlate, dueDate, dueKm, type, note, editing, onDone]);

  const hasMainButton = useMainButton({
    text: saving ? "Saqlanmoqda…" : "Saqlash",
    onClick: submit,
    loading: saving,
  });

  return (
    <>
      <AppBar title={editing ? "Eslatmani tahrirlash" : "Eslatma qo'shish"} onBack={onDone} />
      <div className="screen">
        <label className="label">Eslatma turi</label>
        <div className="cat-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
          {REMINDER_TYPES.map((t) => (
            <button
              key={t.code}
              className={`cat-btn ${type === t.code ? "active" : ""}`}
              onClick={() => setType(t.code)}
            >
              <span className="cat-ico">{t.icon}</span>
              <span className="cat-label">{t.label}</span>
            </button>
          ))}
        </div>

        <label className="label">Qaysi avtomobil?</label>
        {cars.length === 0 ? (
          <div className="muted">Avval avtomobil qo'shing.</div>
        ) : (
          <div className="chip-row">
            {cars.map((c) => (
              <button
                key={c.normalized_plate}
                className={`chip ${selPlate === c.normalized_plate ? "active" : ""}`}
                onClick={() => setSelPlate(c.normalized_plate)}
              >
                {c.display_plate}
              </button>
            ))}
          </div>
        )}

        <label className="label">Muddat (sana)</label>
        <input
          className="input"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />

        <label className="label">yoki masofa (km)</label>
        <input
          className="input"
          inputMode="numeric"
          value={dueKm ? groupDigits(dueKm) : ""}
          onChange={(e) => setDueKm(e.target.value.replace(/\D/g, ""))}
          placeholder="Masalan: 95000"
        />

        <label className="label">Izoh (ixtiyoriy)</label>
        <textarea
          className="textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Qo'shimcha ma'lumot…"
        />

        {err && <div className="error-text">{err}</div>}

        {!hasMainButton && (
          <button className="btn" onClick={submit} disabled={saving}>
            {saving ? "Saqlanmoqda…" : "Saqlash"}
          </button>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Ustaxonalar
// ---------------------------------------------------------------------------

export function externalProfileUrl(
  kind: "telegram" | "instagram" | "website",
  value: string,
): string {
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const handle = trimmed.replace(/^@/, "");
  if (kind === "telegram") return `https://t.me/${handle}`;
  if (kind === "instagram") return `https://instagram.com/${handle}`;
  return `https://${trimmed}`;
}

export function locationUrl(workshop: WorkshopContactOut): string | null {
  const saved = workshop.location_url?.trim() ?? "";
  try {
    const url = new URL(saved);
    const host = url.hostname.toLowerCase();
    const supported = ["http:", "https:"].includes(url.protocol) && (
      host === "maps.app.goo.gl" || host === "goo.gl"
      || host === "google.com" || host.endsWith(".google.com")
      || /^(?:maps\.)?google\.[a-z.]+$/.test(host)
      || host === "yandex.com" || host.endsWith(".yandex.com")
      || host.startsWith("maps.yandex.") || host.startsWith("yandex.")
    );
    if (supported) return saved;
  } catch {
    // Invalid/empty saved URL falls through to provider-neutral coordinates.
  }
  if (workshop.latitude == null || workshop.longitude == null) return null;
  const coords = `${workshop.latitude},${workshop.longitude}`;
  return `geo:${coords}?q=${coords}(${encodeURIComponent(workshop.name)})`;
}

export function workshopDetailRows(workshop: WorkshopContactOut) {
  const location = locationUrl(workshop);
  return [
    workshop.phone && { label: "Telefon", value: formatUzPhone(workshop.phone), action: `tel:${workshop.phone}` },
    workshop.address && { label: "Manzil", value: workshop.address },
    location && { label: "Joylashuv", value: "Xaritada ochish", action: location },
    workshop.work_hours && { label: "Ish vaqti", value: workshop.work_hours },
    workshop.description && { label: "Tavsif", value: workshop.description },
    workshop.telegram && { label: "Telegram", value: workshop.telegram, action: externalProfileUrl("telegram", workshop.telegram) },
    workshop.instagram && { label: "Instagram", value: workshop.instagram, action: externalProfileUrl("instagram", workshop.instagram) },
    workshop.website && { label: "Veb-sayt", value: workshop.website, action: externalProfileUrl("website", workshop.website) },
  ].filter(Boolean) as { label: string; value: string; action?: string }[];
}

function WorkshopsScreen({ push }: { push: (v: View) => void }) {
  const { data, error, loading } = useAsync<WorkshopContactOut[]>(
    () => api.clientWorkshops(),
    [],
  );
  return (
    <>
      <AppBar title="Ustaxonalar" />
      <div className="screen">
        {loading ? <Loader /> : error ? (
          <div className="empty">{error}</div>
        ) : !data?.length ? (
          <div className="empty">Siz hali ustaxonaga ulanmagansiz.</div>
        ) : (
          <div className="workshop-list">
            {data.map((workshop) => (
              <button
                className="workshop-list-item"
                key={workshop.workshop_id}
                onClick={() => push({ name: "workshop", workshop })}
              >
                <span>{workshop.name}</span><span aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export function WorkshopDetailScreen({
  workshop,
  pop,
}: {
  workshop: WorkshopContactOut;
  pop: () => void;
}) {
  const rows = workshopDetailRows(workshop);
  const eligibility = useAsync(
    () => api.clientWorkshopRatingEligibility(workshop.workshop_id),
    [workshop.workshop_id],
  );
  const [showRating, setShowRating] = useState(false);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [ratingError, setRatingError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const values = Array.from({ length: 9 }, (_, index) => 1 + index * 0.5);

  const submitRating = async () => {
    setSaving(true);
    setRatingError(null);
    try {
      await api.clientRateWorkshop(workshop.workshop_id, {
        stars,
        comment: comment.trim() || null,
      });
      setSubmitted(true);
      setShowRating(false);
      haptic("success");
    } catch (error) {
      setRatingError(
        (error as { message?: string }).message || "Bahoni yuborib bo'lmadi.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <AppBar title="Ustaxona" onBack={pop} />
      <div className="screen">
        <h1 className="h1">{workshop.name}</h1>
        <div className="workshop-detail panel">
          {rows.map((row) => row.action ? (
            <button key={row.label} className="workshop-detail-row" onClick={() => openExternal(row.action!)}>
              <span className="k">{row.label}</span><span>{row.value}</span>
            </button>
          ) : (
            <div key={row.label} className="workshop-detail-row">
              <span className="k">{row.label}</span><span>{row.value}</span>
            </div>
          ))}
        </div>
        <div className="rating-action panel">
          <h2 className="h2">Ustaxonani baholash</h2>
          {eligibility.loading ? (
            <div className="muted">Tekshirilmoqda…</div>
          ) : submitted ? (
            <div className="rating-success">Rahmat! Bahoyingiz qabul qilindi.</div>
          ) : eligibility.data?.eligible ? (
            <>
              {!showRating ? (
                <button className="btn" onClick={() => setShowRating(true)}>
                  Ustaxonani baholash
                </button>
              ) : (
                <div className="rating-form">
                  <p className="sub">1 dan 5 gacha, yarim yulduz qadamida tanlang.</p>
                  <div className="rating-options" role="radiogroup" aria-label="Baho">
                    {values.map((value) => (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={stars === value}
                        className={stars === value ? "active" : ""}
                        onClick={() => setStars(value)}
                      >
                        {value.toFixed(1)} ★
                      </button>
                    ))}
                  </div>
                  <label className="label" htmlFor="rating-comment">Izoh (ixtiyoriy)</label>
                  <textarea
                    id="rating-comment"
                    className="textarea"
                    maxLength={1000}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                  />
                  {ratingError && <div className="error-text">{ratingError}</div>}
                  <button className="btn" disabled={saving} onClick={submitRating}>
                    {saving ? "Yuborilmoqda…" : "Bahoni yuborish"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="muted">
              Yangi xizmatdan keyin bu ustaxonani yana baholashingiz mumkin.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

// Settings
// ---------------------------------------------------------------------------

function SettingsScreen({ user }: { user: UserOut }) {
  return (
    <>
      <AppBar title="Sozlamalar" />
      <div className="screen">
        <div className="panel" style={{ marginTop: 14 }}>
          <div className="kv">
            <span className="k">Ism</span>
            <span>{user.full_name}</span>
          </div>
          {user.phone && (
            <div className="kv">
              <span className="k">Telefon</span>
              <span>{formatUzPhone(user.phone)}</span>
            </div>
          )}
        </div>
        <p className="sub" style={{ marginTop: 16 }}>
          AvtoDaftar — shaxsiy avto xarajatlari daftaringiz. Mavzu (ochiq/qorong'i)
          Telegram sozlamalaringizga moslashadi.
        </p>
      </div>
    </>
  );
}
